import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, ArrowLeft, RefreshCw, Loader2, Lock, Inbox } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_TONE = {
  legal: 'bg-amber-100 text-amber-800 border-amber-200',
  'cross-shift': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'off-label': 'bg-indigo-100 text-indigo-800 border-indigo-200',
};

function LedgerPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetch(`${API}/attestation-ledger`)
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);

  const s = data?.summary || { total: 0, verified: 0, tampered: 0 };

  return (
    <>
      <div className="flex items-center justify-end mb-4">
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="ledger-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ledger-total">
          <div className="text-2xl font-bold text-slate-900">{s.total}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Total attestations</div>
        </div>
        <div className="bg-white border border-emerald-200 rounded-xl p-4" data-testid="ledger-verified">
          <div className="text-2xl font-bold text-emerald-700 flex items-center gap-1.5"><ShieldCheck className="w-5 h-5" />{s.verified}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Chain verified</div>
        </div>
        <div className="bg-white border border-red-200 rounded-xl p-4" data-testid="ledger-tampered">
          <div className="text-2xl font-bold text-red-600 flex items-center gap-1.5"><ShieldAlert className="w-5 h-5" />{s.tampered}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Tamper-flagged</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading ledger…</div>
        ) : (data?.entries || []).length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm" data-testid="ledger-empty">No attestations recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-2.5">Encounter</th>
                  <th className="px-4 py-2.5">Type</th>
                  <th className="px-4 py-2.5">Physician</th>
                  <th className="px-4 py-2.5">Timestamp (UTC)</th>
                  <th className="px-4 py-2.5">SHA-256</th>
                  <th className="px-4 py-2.5">Integrity</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ledger-row-${i}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{e.encounter_id}</td>
                    <td className="px-4 py-2.5"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${TYPE_TONE[e.type] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{e.type}</span></td>
                    <td className="px-4 py-2.5 text-slate-600">{e.physician || '—'}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{(e.timestamp || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-4 py-2.5 font-mono text-[10px] text-slate-400">{(e.hash || '').slice(0, 18)}…</td>
                    <td className="px-4 py-2.5">
                      {e.chain_ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium"><ShieldCheck className="w-3.5 h-3.5" /> verified</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium"><ShieldAlert className="w-3.5 h-3.5" /> tampered</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mt-4">Board / insurer-facing audit artifact. Each record's SHA-256 is recomputed from its canonical fields and compared to the stored seal.</p>
    </>
  );
}

function AccessRequestsPanel() {
  const [passcode, setPasscode] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    setLoading(true);
    try {
      const r = await fetch(`${API}/access-requests?passcode=${encodeURIComponent(passcode)}`);
      if (r.status === 401) { setError('Invalid passcode.'); setData(null); setLoading(false); return; }
      const d = await r.json();
      setData(d);
    } catch {
      setError('Could not load access requests.');
    }
    setLoading(false);
  };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="access-auth-bar">
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Lock className="w-4 h-4" /> Admin only</div>
        <input
          type="password"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder="Admin passcode"
          data-testid="access-passcode-input"
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
        />
        <button onClick={load} disabled={loading} data-testid="access-load-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />} View requests
        </button>
        {error && <span className="text-xs text-red-600" data-testid="access-auth-error">{error}</span>}
      </div>

      {data && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {(data.requests || []).length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm" data-testid="access-requests-empty">No access requests yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="access-requests-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2.5">Name</th>
                    <th className="px-4 py-2.5">Organization</th>
                    <th className="px-4 py-2.5">Email</th>
                    <th className="px-4 py-2.5">Role</th>
                    <th className="px-4 py-2.5">Surface</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Received (UTC)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.map((r, i) => (
                    <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`access-request-row-${i}`}>
                      <td className="px-4 py-2.5 text-slate-800 font-medium">{r.name}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.organization || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.email}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.role || '—'}</td>
                      <td className="px-4 py-2.5"><span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-200">{r.surface || '—'}</span></td>
                      <td className="px-4 py-2.5"><span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200">{r.status || 'pending'}</span></td>
                      <td className="px-4 py-2.5 text-slate-500 text-xs">{(r.timestamp || '').slice(0, 19).replace('T', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default function AttestationLedgerPage() {
  const [tab, setTab] = useState('ledger');

  const TabBtn = ({ id, label }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`ledger-tab-${id}`}
      className={`px-4 py-2 text-sm font-semibold rounded-t-lg border-b-2 -mb-px transition-colors ${
        tab === id ? 'border-slate-800 text-slate-900 bg-white' : 'border-transparent text-slate-500 hover:text-slate-800'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" style={{ fontFamily: "'IBM Plex Sans',sans-serif" }}>
      <div className="max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-6" data-testid="ledger-back">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>

        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Work Sans',sans-serif" }}>Attestation Ledger</h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">Read-only audit trail across all hash-chained attestation surfaces (legal · cross-shift · off-label). FRE 902(13) · tamper-evident.</p>

        <div className="flex gap-1.5 border-b border-slate-200 mb-6" data-testid="ledger-tabs">
          <TabBtn id="ledger" label="Attestation Ledger" />
          <TabBtn id="access-requests" label="Access Requests" />
        </div>

        {tab === 'ledger' ? <LedgerPanel /> : <AccessRequestsPanel />}
      </div>
    </div>
  );
}
